import React, { useEffect, useMemo, useState } from 'react';
import { DocumentNode, useQuery } from '@apollo/client';
import { debounce } from '../../../utils/debounce';
import {
  generatePaginationMessage,
  PAGINATION_STATUS,
} from '../utils/PageStatus';
import { usePaginationContext } from '../PaginationContext';
import useIsComponentMounted from '../../../pages/hooks/useIsComponentMounted';
import { createDynamicApolloClient } from '../../../apollo/getApolloClient';
import { USER_SEARCH_QUERY } from '../../../apollo/queries/adminUsers/getAdmins.queries';
import { AdminRole } from '../../../pages/AddAdminUser/hooks/useAddAdminUser';
import { SHARED_TENANT } from '../../../utils/constants';

type Props = {
  query: DocumentNode;
  responseField: string;
  pageSize?: number;
  currentPage?: number;
  isTenantSpecific?: boolean;
  variables?: { [key: string]: any };
  pageStatusFormat?: string;
};

interface PaginationResponse {
  pageSize: number;
  totalCount: number;
}

const defaultPageStatusFormat = `Showing records ${PAGINATION_STATUS.FROM} - ${PAGINATION_STATUS.TO} of ${PAGINATION_STATUS.TOTAL}`;

const usePagination = <T = any>({
  query,
  responseField,
  isTenantSpecific,
  pageSize = 10,
  currentPage: defaultCurrentPage = 1,
  variables,
  pageStatusFormat = defaultPageStatusFormat,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(defaultCurrentPage);
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStats, setPageStats] = useState('');
  const [tenantCode, setTenant] = useState('');
  const [pageSizeState, setPageSizeState] = useState(pageSize);

  const isMounted = useIsComponentMounted();
  const context = usePaginationContext();

  // Used to call search method via Context API
  useEffect(() => {
    if (isMounted) {
      if (context.searchText === '') {
        setCurrentPage(1);
      }

      onSearchDebounce(context.searchText);
    }
  }, [context.searchText]);

  const getFilters = (filters: any) => {
    if (isTenantSpecificCall(isTenantSpecific, tenantCode)) {
      return {
        page: currentPage - 1,
        size: pageSizeState,
        searchTerm: search,
        roles: [AdminRole.ORGANIZATION_ADMIN, AdminRole.ALEF_ORG_SUPPORT],
        tenant: tenantCode,
      };
    }
    return {
      pageNumber: currentPage - 1,
      pageSize: pageSizeState,
      search,
      ...filters,
    };
  };

  const isTenantSpecificCall = (isTenantSpecific: any, tenantCode: string) => {
    return (
      isTenantSpecific && tenantCode !== '' && tenantCode !== SHARED_TENANT
    );
  };

  const client = useMemo(() => {
    return createDynamicApolloClient(tenantCode);
  }, [tenantCode]);

  const { loading, error, data, refetch } = useQuery<T>(
    isTenantSpecificCall(isTenantSpecific, tenantCode)
      ? USER_SEARCH_QUERY
      : query,
    {
      variables: getFilters(variables),
      onCompleted: (data) => {
        const pageData = (data as any)[responseField] as PaginationResponse;

        const { totalCount } = isTenantSpecificCall(
          isTenantSpecific,
          tenantCode,
        )
          ? {
              totalCount: Number(localStorage.getItem('totalCount')),
            }
          : pageData;
        const totalPages = Math.ceil(totalCount / pageSizeState);
        const from = (currentPage - 1) * pageSizeState + 1;
        const to =
          currentPage * pageSizeState > totalCount
            ? totalCount
            : currentPage * pageSizeState;

        setPageStats(
          generatePaginationMessage(pageStatusFormat, {
            from,
            to,
            total: totalCount,
          }),
        );
        setTotalPages(totalPages);
        setTotalCount(totalCount);
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      client: isTenantSpecificCall(isTenantSpecific, tenantCode)
        ? client
        : undefined,
    },
  );

  const onSearchDebounce = React.useCallback(
    debounce((value: string) => {
      setPageStats('');
      setSearch(value);
      setCurrentPage(1);
    }),
    [],
  );

  const onTenantDebounce = React.useCallback(
    debounce((value: string) => {
      setPageStats('');
      setTenant(value);
      setSearch('');
      setCurrentPage(1);
    }),
    [],
  );

  const totalCountFromLocalStorage = Number(localStorage.getItem('totalCount'));
  const pages = Math.ceil(totalCountFromLocalStorage / pageSizeState);

  const setPageSize = React.useCallback((size: number) => {
    setPageSizeState(size);
    setCurrentPage(1);
    setPageStats('');
  }, []);

  return {
    queryData: {
      loading,
      error,
      data: data as T,
      refetch,
    },
    pageData: {
      currentPage,
      search,
      totalPages: isTenantSpecificCall(isTenantSpecific, tenantCode)
        ? pages
        : totalPages,
      totalCount,
      pageSize: pageSizeState,
      pageStats,
      tenantCode,
    },
    actions: {
      setCurrentPage,
      setSearch,
      setPageSize,
      onSearch: onSearchDebounce,
      onTenant: onTenantDebounce,
      setTenant,
    },
  };
};

export default usePagination;
